<script setup>
import {useStore} from "vuex";
import {computed, onMounted, ref, watch} from "vue";
import ViewerToolbar from "@/components/viewer/ViewerToolbar.vue";
import ModelComments from "@/components/viewer/ModelComments.vue";
import ModelFilters from "@/components/viewer/ModelFilters.vue";

// Vuex
const store        = useStore();
// Variables
const loadingModel = ref(true);
const collapsed    = ref(store.state.isModelCollapsed);
// Computed properties
const activeRightPanelTab = computed(() => store.getters.getViewerRightPanelTab);

// Methods
const loadModel    = () => {
  loadingModel.value   = true;
  // Load model viewer
  const modelContainer = document.querySelector('#client-model-viewer');
  store.dispatch('initViewer', {
    container: modelContainer
  }).catch((error) => {
    console.error('Error while loading model viewer:', error);
  }).finally(() => {
    loadingModel.value = false;
  });
};
// Watchers
watch(collapsed, (newValue) => {
  store.dispatch("handleIsModelCollapsed", newValue);

  if (!collapsed.value) {
    loadModel()
  }
});

// Lifecycle hooks
onMounted(
    () => {
      if (!collapsed.value) {
        loadModel();
      }
    }
);

</script>

<template lang="html">
  <v-card class="model-card" elevation="3">
    <v-card-text class="model-card-text">
      <v-expand-transition>
        <v-row v-show="!collapsed">
          <v-col>
            <v-row>
              <v-col class="model-viewer-column-left">
                <ViewerToolbar/>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="9" class="model-viewer-column-main">
                <v-container id="client-model-viewer-container" class="model-viewer-container">
                  <div id="client-model-viewer"></div>

                  <transition name="fade">
                    <div v-if="loadingModel" class="viewer-loading-overlay">
                      <v-progress-circular indeterminate color="primary" size="48" />
                      <div class="viewer-loading-text">Loading model…</div>
                    </div>
                  </transition>
                </v-container>
              </v-col>
              <v-col cols="3" class="model-viewer-column-right">
                <v-sheet class="mx-auto">
                  <ModelComments v-if="activeRightPanelTab === 'comments'" :loading-model="loadingModel"></ModelComments>
                  <ModelFilters v-else :loading-model="loadingModel"></ModelFilters>
                </v-sheet>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-expand-transition>
      <v-row justify="center">
        <v-btn @click="collapsed = !collapsed">
          <v-icon>{{ collapsed ? 'mdi-chevron-down' : 'mdi-chevron-up' }}</v-icon>
        </v-btn>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<style scoped>
@import '@/assets/styles/components/viewer/ModelViewer.css';

.model-card {
    margin-bottom: 20px;
    border-radius: 16px;
}

.model-card-text {
    padding: 0rem;
    padding-top: 0.75rem;
}

#client-model-viewer-container {
  position: relative;
  height: 512px;
}

#client-model-viewer {
  position: absolute;
  inset: 0;
}

.viewer-loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 10;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;

  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(2px);
  text-align: center;
}

.viewer-loading-text {
  font-size: 0.95rem;
  text-align: center;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 180ms ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

</style>
